import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { ChartOfAccountLeafSelect } from "@/components/accounts/ChartOfAccountLeafSelect";
import {
  AccountReportFilterActions,
  AccountReportPageHeader,
  AccountReportTableShell,
} from "@/components/accounts/AccountReportShell";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import { renderBalance } from "@/lib/accounts/ledgerReportDisplay";
import { formatReportDate, money } from "@/lib/accounts/ledgerReportUtils";
import {
  downloadCompanyLedgerExcel,
  downloadCompanyLedgerPdf,
  fetchCompanyLedger,
  formatLedgerAccountLabel,
  type CompanyLedger,
} from "@/lib/accounts/companyLedgerStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { BookOpen } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

function validationMessage(error: unknown): string | null {
  if (!isAxiosError(error) || error.response?.status !== 422) return null;
  const data = error.response?.data as {
    message?: string;
    errors?: Record<string, string[]>;
  };
  const firstFieldError = data?.errors
    ? Object.values(data.errors).flat().find((msg) => Boolean(msg))
    : null;
  return firstFieldError || data?.message || "Invalid filter selection.";
}

const CompanyLedgerPage = () => {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView = can(user, "chart_of_account_can_view_any") || can(user, "bank_can_view_any");

  const [isForbidden, setIsForbidden] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<"pdf" | "excel" | null>(null);
  const [hasApplied, setHasApplied] = useState(false);
  const [ledger, setLedger] = useState<CompanyLedger | null>(null);
  const [accountId, setAccountId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const currencySymbol = user?.company_branding?.currency?.symbol?.trim() || "";
  const hasAccount = accountId.trim() !== "";
  const isDailyMode = ledger?.mode === "daily_vouchers";

  const activeFilters = useMemo(
    () => ({
      accountId: hasAccount ? accountId : null,
      dateFrom: dateFrom || null,
      dateTo: dateTo || null,
    }),
    [hasAccount, accountId, dateFrom, dateTo],
  );

  useEffect(() => {
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("auth_user_updated", onAuth);
    return () => window.removeEventListener("auth_user_updated", onAuth);
  }, []);

  const applyFilters = useCallback(async () => {
    if (!canView) return;

    if (!hasAccount && (!dateFrom || !dateTo)) {
      toast.error("Select from and to dates for daily vouchers, or select an account for ledger.");
      return;
    }

    setLoading(true);
    setIsForbidden(false);
    try {
      const data = await fetchCompanyLedger(activeFilters);
      setLedger(data);
      setHasApplied(true);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 403) {
        setIsForbidden(true);
        return;
      }
      const message = validationMessage(error);
      if (message) {
        toast.error(message);
        return;
      }
      console.error("[GeneralLedger] Load failed", error);
      toast.error("Could not load report.");
    } finally {
      setLoading(false);
    }
  }, [canView, hasAccount, dateFrom, dateTo, activeFilters]);

  const exportReport = useCallback(
    async (format: "pdf" | "excel") => {
      if (!hasApplied || exporting) return;
      setExporting(format);
      try {
        if (format === "pdf") {
          await downloadCompanyLedgerPdf(activeFilters);
          toast.success("PDF downloaded.");
        } else {
          await downloadCompanyLedgerExcel(activeFilters);
          toast.success("Excel downloaded.");
        }
      } catch (error) {
        console.error(`[GeneralLedger] ${format} export failed`, error);
        toast.error(format === "pdf" ? "Could not download PDF." : "Could not download Excel.");
      } finally {
        setExporting(null);
      }
    },
    [hasApplied, exporting, activeFilters],
  );

  const resetFilters = () => {
    setAccountId("");
    setDateFrom("");
    setDateTo("");
    setLedger(null);
    setHasApplied(false);
  };

  const accountLabel = ledger?.account
    ? `${ledger.account.code ? `${ledger.account.code} — ` : ""}${ledger.account.name}`
    : null;

  const colSpan = isDailyMode ? 5 : 6;

  if (!canView || isForbidden) {
    return (
      <Layout>
        <PermissionDenied />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5">
        <AccountReportPageHeader
          icon={<BookOpen className="h-5 w-5 text-primary" />}
          title="General Ledger"
          description="Select an account for its ledger with opening balance, or use from/to dates only to view daily voucher transactions."
        />

        <div className="rounded-2xl border border-border/55 bg-card/80 p-4 shadow-sm ring-1 ring-border/25">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs font-semibold text-foreground">
                Account <span className="font-normal text-muted-foreground">(optional)</span>
              </Label>
              <ChartOfAccountLeafSelect
                value={accountId}
                onChange={setAccountId}
                placeholder="All accounts — daily vouchers"
                allowClear
                clearLabel="All accounts — daily vouchers"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">
                From date{!hasAccount ? " *" : ""}
              </Label>
              <DatePartsPicker
                value={dateFrom}
                onChange={setDateFrom}
                fromYear={1980}
                toYear={new Date().getFullYear() + 1}
                maxDate={dateTo ? new Date(`${dateTo}T00:00:00`) : undefined}
                placeholder="Select from date"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">
                To date{!hasAccount ? " *" : ""}
              </Label>
              <DatePartsPicker
                value={dateTo}
                onChange={setDateTo}
                fromYear={1980}
                toYear={new Date().getFullYear() + 1}
                minDate={dateFrom ? new Date(`${dateFrom}T00:00:00`) : undefined}
                placeholder="Select to date"
                align="end"
              />
            </div>
          </div>

          <AccountReportFilterActions
            hasApplied={hasApplied}
            loading={loading}
            exporting={exporting}
            onApply={() => void applyFilters()}
            onReset={resetFilters}
            onExport={(format) => void exportReport(format)}
            statusText={
              hasApplied ? (
                <>
                  {isDailyMode ? (
                    <>Showing <span className="font-semibold text-foreground">daily vouchers</span></>
                  ) : accountLabel ? (
                    <>
                      Showing ledger for <span className="font-semibold text-foreground">{accountLabel}</span>
                    </>
                  ) : (
                    "Showing account ledger"
                  )}
                  {dateFrom ? (
                    <>
                      {" · From "}
                      <span className="font-semibold text-foreground">{formatReportDate(dateFrom)}</span>
                    </>
                  ) : null}
                  {dateTo ? (
                    <>
                      {" · To "}
                      <span className="font-semibold text-foreground">{formatReportDate(dateTo)}</span>
                    </>
                  ) : null}
                </>
              ) : (
                "Select an account for ledger, or from/to dates only for daily vouchers, then Apply filter."
              )
            }
          />
        </div>

        <AccountReportTableShell
          loading={loading}
          hasApplied={hasApplied}
          notAppliedMessage="No report loaded yet. Choose an account or a date range, then apply."
        >
          {!ledger ? (
            <p className="py-12 text-center text-sm text-muted-foreground">No data.</p>
          ) : (
            <div className="overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date / Voucher #</TableHead>
                    {isDailyMode ? <TableHead>Account</TableHead> : null}
                    <TableHead>Particulars</TableHead>
                    <TableHead>From / To</TableHead>
                    <TableHead className="text-right">Debit{currencySymbol ? ` (${currencySymbol})` : ""}</TableHead>
                    <TableHead className="text-right">Credit{currencySymbol ? ` (${currencySymbol})` : ""}</TableHead>
                    {!isDailyMode ? (
                      <TableHead className="text-right">
                        Balance{currencySymbol ? ` (${currencySymbol})` : ""}
                      </TableHead>
                    ) : null}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!isDailyMode ? (
                    <TableRow className="bg-muted/30">
                      <TableCell className="font-semibold text-foreground" colSpan={3}>
                        Opening Balance
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground" />
                      <TableCell className="text-right tabular-nums text-muted-foreground" />
                      <TableCell className="text-right tabular-nums font-semibold">
                        {renderBalance(ledger.totals.opening_balance, ledger.totals.opening_balance_side)}
                      </TableCell>
                    </TableRow>
                  ) : null}

                  {ledger.lines.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={colSpan} className="py-10 text-center text-sm text-muted-foreground">
                        No posted entries in this period.
                      </TableCell>
                    </TableRow>
                  ) : (
                    ledger.lines.map((line) => (
                      <TableRow key={line.id}>
                        <TableCell className="min-w-[8rem] whitespace-nowrap">
                          <div className="text-sm text-foreground">{formatReportDate(line.date)}</div>
                          <div className="font-mono text-[11px] text-muted-foreground">{line.voucher || ""}</div>
                          {line.voucher_type ? (
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                              {line.voucher_type}
                            </div>
                          ) : null}
                        </TableCell>
                        {isDailyMode ? (
                          <TableCell className="min-w-[9rem] max-w-[14rem] text-xs leading-snug text-foreground">
                            {formatLedgerAccountLabel(line)}
                          </TableCell>
                        ) : null}
                        <TableCell className="min-w-[10rem] max-w-[18rem]">
                          <div className="space-y-0.5">
                            {line.particulars.length > 0 ? (
                              line.particulars.map((text, idx) => (
                                <p
                                  key={`${line.id}-p-${idx}`}
                                  className={cn(
                                    "leading-snug",
                                    idx === 0
                                      ? "text-xs font-medium text-foreground"
                                      : "text-[11px] text-muted-foreground",
                                  )}
                                >
                                  {text}
                                </p>
                              ))
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="min-w-[8rem] max-w-[14rem] text-xs leading-snug text-muted-foreground">
                          {line.from_to || ""}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-emerald-700 dark:text-emerald-400">
                          {money(line.debit, true)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-rose-700 dark:text-rose-400">
                          {money(line.credit, true)}
                        </TableCell>
                        {!isDailyMode ? (
                          <TableCell className="text-right tabular-nums font-medium">
                            {renderBalance(line.balance, line.balance_side)}
                          </TableCell>
                        ) : null}
                      </TableRow>
                    ))
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell
                      colSpan={isDailyMode ? 4 : 3}
                      className="font-semibold text-foreground"
                    >
                      {isDailyMode ? "Total" : "Closing Balance"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">
                      {money(ledger.totals.debit)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">
                      {money(ledger.totals.credit)}
                    </TableCell>
                    {!isDailyMode ? (
                      <TableCell className="text-right tabular-nums font-semibold">
                        {renderBalance(ledger.totals.balance, ledger.totals.balance_side)}
                      </TableCell>
                    ) : null}
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          )}
        </AccountReportTableShell>
      </div>
    </Layout>
  );
};

export default CompanyLedgerPage;
